'use client';

import React, { useCallback, useContext, useEffect } from 'react';

export interface ExitNavigationContextType {
  addInterceptionObserver: (callback: () => void) => void;
  removeInterceptionObserver: (callback: () => void) => void;
}

const ExitNavigationContext = React.createContext<ExitNavigationContextType | undefined>(undefined);

export const ExitInterceptorProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [observersCount, setObserversCount] = React.useState(0);

  const addInterceptionObserver = (observer: () => void) => {
    window.addEventListener('NavigationInterceptorContext', observer);
    setObserversCount(observersCount + 1);
  };

  const removeInterceptionObserver = (observer: () => void) => {
    window.removeEventListener('NavigationInterceptorContext', observer);
    setObserversCount(Math.max(observersCount - 1, 0));
  };

  const onExit = useCallback(() => {
    window.dispatchEvent(new CustomEvent('NavigationInterceptorContext', {}));
    return observersCount > 0;
  }, [observersCount]);

  const value: ExitNavigationContextType = {
    addInterceptionObserver,
    removeInterceptionObserver
  };

  const context = useContext(ExitNavigationContext);

  useEffect(() => {
    if (context !== undefined) {
      return;
    }

    window.CitiboxCourierWebApp.addExitObserver(() => {
      return onExit();
    });

    return () => {
      window.CitiboxCourierWebApp.clearExitObservers();
    };
  }, [context, onExit]);

  if (context !== undefined) {
    return <>{children}</>;
  } else {
    return <ExitNavigationContext.Provider value={value}>{children}</ExitNavigationContext.Provider>;
  }
};

export const useExitNavigationInterceptor = (allowUndefined?: boolean) => {
  const context = useContext(ExitNavigationContext);

  if (allowUndefined !== true && context === undefined) {
    throw new Error('useExitNavigationInterceptor must be a child of NavigationProvider');
  }

  return context;
};
